/**
 * Ability Context (CASL)
 *
 * Loads abilities from backend /api/abilities/me.
 * Falls back to local computation.
 * Provides: ability, Can component, useCan hook.
 */

import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import { createMongoAbility, AbilityBuilder } from "@casl/ability";
import { createContextualCan } from "@casl/react";
import { useAuth } from "context/AuthContext";
import logger from "services/logger";
import { endpoints, API_TIMEOUT } from "config/api";

const AbilityContext = createContext(null);
AbilityContext.displayName = "AbilityContext";

// CASL subjects and actions — must match backend
export const SUBJECTS = {
  ALL: "all",
  USER: "User",
  ROLE: "Role",
  SAMPLE: "Sample",
  INVITE_CODE: "InviteCode",
  NOTIFICATION: "Notification",
  ADMIN_NOTIFICATION: "AdminNotification",
  NOTIFICATION_TEMPLATE: "NotificationTemplate",
  MENU: "Menu",
  METRIC_USERS: "MetricUsers",
  METRIC_SYSTEM: "MetricSystem",
  PORTAL: "Portal",
  PORTAL_PAGE: "PortalPage",
  PORTAL_TRANSLATION: "PortalTranslation",
};

export const ACTIONS = {
  CREATE: "create",
  READ: "read",
  UPDATE: "update",
  DELETE: "delete",
  MANAGE: "manage",
  INVITE: "invite",
  SUSPEND: "suspend",
  UNSUSPEND: "unsuspend",
  IMPERSONATE: "impersonate",
  EXPORT: "export",
  ASSIGN: "assign",
  PUBLISH: "publish",
};

/**
 * Local fallback ability builder.
 */
function defineAbilitiesFor(user) {
  const { can, cannot, build } = new AbilityBuilder(createMongoAbility);
  const { role, adminAccess, uid } = user;

  if (role === "admin") {
    switch (adminAccess) {
      case "superadmin":
        can(ACTIONS.MANAGE, SUBJECTS.ALL);
        break;
      case "platformadmin":
        can(ACTIONS.MANAGE, SUBJECTS.ALL);
        cannot(ACTIONS.DELETE, SUBJECTS.USER, { role: "admin" });
        break;
      case "standard":
        can(ACTIONS.READ, SUBJECTS.ALL);
        can(ACTIONS.UPDATE, SUBJECTS.ALL);
        break;
      default: // readonly
        can(ACTIONS.READ, SUBJECTS.ALL);
        break;
    }
  } else {
    // user role
    can(ACTIONS.READ, SUBJECTS.USER, { uid });
    can(ACTIONS.UPDATE, SUBJECTS.USER, { uid });
    can(ACTIONS.MANAGE, SUBJECTS.SAMPLE, { createdBy: uid });
    can(ACTIONS.READ, SUBJECTS.SAMPLE);
    can(ACTIONS.READ, SUBJECTS.MENU);
  }

  return build();
}

const defaultAbility = createMongoAbility([]);

export function AbilityProvider({ children }) {
  const { currentUser, role, adminAccess, getIdToken } = useAuth();
  const [ability, setAbility] = useState(defaultAbility);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isReady, setIsReady] = useState(false);

  const loadAbilities = useCallback(async () => {
    if (!currentUser) {
      setAbility(defaultAbility);
      setModules([]);
      setLoading(false);
      setIsReady(true);
      return;
    }

    try {
      const token = await getIdToken();
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT);

      const res = await fetch(endpoints.abilities.me, {
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data = await res.json();
      if (data.success && data.rules) {
        const dynamicAbility = createMongoAbility(data.rules);
        setAbility(dynamicAbility);
        setModules(data.modules || []);
        logger.debug("[Ability]", `Loaded ${data.rules.length} rules, ${(data.modules || []).length} modules from backend`);
      } else {
        throw new Error("Invalid response");
      }
    } catch (err) {
      logger.warn("[Ability]", "Backend fetch failed, using local fallback:", err.message);
      const localAbility = defineAbilitiesFor({
        role: role || "user",
        adminAccess,
        uid: currentUser.uid,
      });
      setAbility(localAbility);
      // No modules available in fallback — show all routes
      setModules(null);
    } finally {
      setLoading(false);
      setIsReady(true);
    }
  }, [currentUser, role, adminAccess, getIdToken]);

  useEffect(() => {
    loadAbilities();
  }, [loadAbilities]);

  const value = useMemo(
    () => ({ ability, modules, loading, isReady, reload: loadAbilities }),
    [ability, modules, loading, isReady, loadAbilities]
  );

  return <AbilityContext.Provider value={value}>{children}</AbilityContext.Provider>;
}

export function useAbility() {
  const context = useContext(AbilityContext);
  if (!context) throw new Error("useAbility must be used within AbilityProvider");
  return context.ability;
}

export function useModules() {
  const context = useContext(AbilityContext);
  if (!context) throw new Error("useModules must be used within AbilityProvider");
  return context.modules;
}

export function useAbilityLoading() {
  const context = useContext(AbilityContext);
  if (!context) throw new Error("useAbilityLoading must be used within AbilityProvider");
  return { loading: context.loading, isReady: context.isReady };
}

export function useCan(action, subject) {
  const ability = useAbility();
  return ability.can(action, subject);
}

export const Can = createContextualCan(AbilityContext);

export default AbilityContext;
